import { CheckSquare, Square } from 'lucide-react-native'
import { type Control, type FieldValues, type Path, useController } from 'react-hook-form'
import { StyleSheet, View, type ViewProps } from 'react-native'
import { Chip, HelperText, Text, useTheme } from 'react-native-paper'
import { WEEKDAY_LABELS } from '@/types/workingHours'

export type WorkingDaysSelectorProps<T extends FieldValues = Record<string, unknown>> = {
    name: Path<T>
    control: Control<T>
    label: string
    hint?: string
    disabled?: boolean
    wrapperProps?: ViewProps
}

export function WorkingDaysSelector<T extends FieldValues = Record<string, unknown>>({
    name,
    control,
    label,
    hint,
    disabled = false,
    wrapperProps = {},
}: WorkingDaysSelectorProps<T>) {
    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
    })
    const theme = useTheme()
    const selectedDays: number[] = field.value || []

    const toggleDay = (day: number) => {
        if (disabled) return
        const newDays = selectedDays.includes(day)
            ? selectedDays.filter(d => d !== day)
            : [...selectedDays, day].sort((a, b) => a - b)
        field.onChange(newDays)
    }

    return (
        <View style={[styles.wrapper, wrapperProps.style]} {...wrapperProps}>
            <Text
                variant="bodySmall"
                style={[styles.label, { color: theme.colors.onSurfaceVariant }]}
            >
                {label}
            </Text>
            <View style={styles.daysContainer}>
                {Object.entries(WEEKDAY_LABELS).map(([dayNum, dayName]) => {
                    const day = Number(dayNum)
                    const isSelected = selectedDays.includes(day)
                    const IconComponent = isSelected ? CheckSquare : Square
                    return (
                        <Chip
                            key={day}
                            selected={isSelected}
                            onPress={() => toggleDay(day)}
                            disabled={disabled}
                            style={styles.dayChip}
                            mode={isSelected ? 'flat' : 'outlined'}
                            showSelectedCheck={false}
                            icon={() => (
                                <IconComponent
                                    size={16}
                                    color={
                                        isSelected
                                            ? theme.colors.primary
                                            : theme.colors.onSurfaceVariant
                                    }
                                />
                            )}
                        >
                            {dayName}
                        </Chip>
                    )
                })}
            </View>
            {hint && !error ? <HelperText type="info">{hint}</HelperText> : null}
            {error ? <HelperText type="error">{error.message}</HelperText> : null}
        </View>
    )
}

const styles = StyleSheet.create({
    wrapper: {
        marginBottom: 8,
    },
    label: {
        fontSize: 12,
        marginBottom: 8,
    },
    daysContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    dayChip: {
        marginRight: 0,
        zIndex: undefined,
    },
})
